import { supabase } from '../lib/supabase';

const REGIONAL_TEAM_BUCKET = 'region-associations';

const resolveRegionalTeamLogo = (url, path) => {
  if (url && typeof url === 'string' && url.trim()) return url.trim();
  if (path && typeof path === 'string' && path.trim()) {
    const {
      data: { publicUrl },
    } = supabase.storage.from(REGIONAL_TEAM_BUCKET).getPublicUrl(path.trim());
    return publicUrl || null;
  }
  return null;
};

const GAME_SELECT = `
  *,
  home_team:regional_teams!home_team_id(id, team_name, logo_url, logo_path),
  away_team:regional_teams!away_team_id(id, team_name, logo_url, logo_path)
`;

const normalizeGame = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    groupId: row.group_id,
    homeTeamId: row.home_team_id,
    awayTeamId: row.away_team_id,
    scheduledDate: row.scheduled_date,
    venue: row.venue,
    gameStatus: row.game_status,
    homeScore: row.home_score,
    awayScore: row.away_score,
    matchNotes: row.match_notes,
    stageName: row.stage_name,
    homeTeam: row.home_team
      ? {
          id: row.home_team.id,
          teamName: row.home_team.team_name,
          logoUrl: resolveRegionalTeamLogo(row.home_team.logo_url, row.home_team.logo_path),
        }
      : null,
    awayTeam: row.away_team
      ? {
          id: row.away_team.id,
          teamName: row.away_team.team_name,
          logoUrl: resolveRegionalTeamLogo(row.away_team.logo_url, row.away_team.logo_path),
        }
      : null,
    createdAt: row.created_at,
  };
};

const normalizeTournament = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    season: row.season,
    description: row.description,
    tournamentStatus: row.tournament_status,
    startDate: row.start_date,
    endDate: row.end_date,
    maxTeams: row.max_teams,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

const normalizeGroup = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    name: row.name,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
  };
};

const normalizeTournamentTeam = (row) => {
  if (!row) return null;
  const regionalTeam = row.regional_team;
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    regionalTeamId: row.regional_team_id,
    groupId: row.group_id,
    isActive: row.is_active,
    createdAt: row.created_at,
    team: regionalTeam
      ? {
          id: regionalTeam.id,
          teamName: regionalTeam.team_name,
          logoUrl: resolveRegionalTeamLogo(regionalTeam.logo_url, regionalTeam.logo_path),
        }
      : null,
  };
};

const normalizeTournamentDetail = (row) => {
  if (!row) return null;
  const groups = (row.tournament_groups || [])
    .map(normalizeGroup)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const teams = (row.tournament_teams || []).map(normalizeTournamentTeam);
  return {
    ...normalizeTournament(row),
    teams,
    groups,
  };
};

const normalizeSelectedOfficial = (row) => {
  if (!row) return null;
  const profile = row.official?.user_profile;
  return {
    id: row.id,
    officialId: row.official_id,
    assignedRole: row.assigned_role,
    notes: row.notes,
    isActive: row.is_active,
    official: row.official
      ? {
          id: row.official.id,
          fullName: profile?.full_name || 'Unknown Official',
          specialization: row.official.specialization,
          licenseNumber: row.official.license_number,
          email: profile?.email,
          phone: profile?.phone,
          avatarUrl: profile?.avatar_url,
        }
      : null,
    createdAt: row.created_at,
  };
};

export const tournamentService = {
  async getGames() {
    const { data, error } = await supabase
      .from('tournament_games')
      .select(GAME_SELECT)
      .order('scheduled_date', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load tournament games');
    return (data || []).map(normalizeGame);
  },

  async getGamesByTournament(tournamentId) {
    if (!tournamentId) return [];

    const { data, error } = await supabase
      .from('tournament_games')
      .select(GAME_SELECT)
      .eq('tournament_id', tournamentId)
      .order('scheduled_date', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load tournament games');
    return (data || []).map(normalizeGame);
  },

  async getGamesByTeam(teamId) {
    if (!teamId) return [];

    const { data, error } = await supabase
      .from('tournament_games')
      .select(GAME_SELECT)
      .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
      .order('scheduled_date', { ascending: false });

    if (error) throw new Error(error.message || 'Failed to load team games');
    return (data || []).map(normalizeGame);
  },

  async getGameById(gameId) {
    if (!gameId) return null;

    const { data, error } = await supabase
      .from('tournament_games')
      .select(GAME_SELECT)
      .eq('id', gameId)
      .maybeSingle();

    if (error) throw new Error(error.message || 'Failed to load tournament game');
    return normalizeGame(data);
  },

  async createGame(gameData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    if (!gameData.homeTeamId || !gameData.awayTeamId) {
      throw new Error('Home and away teams are required');
    }
    if (gameData.homeTeamId === gameData.awayTeamId) {
      throw new Error('Home and away teams must be different');
    }

    const payload = {
      home_team_id: gameData.homeTeamId,
      away_team_id: gameData.awayTeamId,
      scheduled_date: gameData.scheduledDate || null,
      venue: gameData.venue?.trim() || null,
      game_status: gameData.gameStatus || 'scheduled',
      created_by: user.id,
    };

    if (gameData.tournamentId !== undefined) payload.tournament_id = gameData.tournamentId || null;
    if (gameData.groupId !== undefined) payload.group_id = gameData.groupId || null;
    if (gameData.matchNotes !== undefined) payload.match_notes = gameData.matchNotes || null;
    if (gameData.stageName !== undefined) payload.stage_name = gameData.stageName || null;

    const { data, error } = await supabase
      .from('tournament_games')
      .insert(payload)
      .select(GAME_SELECT)
      .single();

    if (error) throw new Error(error.message || 'Failed to create tournament game');
    return normalizeGame(data);
  },

  async createGamesBatch(games) {
    if (!games || games.length === 0) return [];

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const rows = games.map((gameData) => {
      if (!gameData.homeTeamId || !gameData.awayTeamId) {
        throw new Error('Home and away teams are required for every fixture');
      }
      return {
        tournament_id: gameData.tournamentId || null,
        group_id: gameData.groupId || null,
        home_team_id: gameData.homeTeamId,
        away_team_id: gameData.awayTeamId,
        scheduled_date: gameData.scheduledDate || null,
        venue: gameData.venue?.trim() || null,
        game_status: gameData.gameStatus || 'scheduled',
        match_notes: gameData.matchNotes || null,
        stage_name: gameData.stageName || null,
        created_by: user.id,
      };
    });

    const { data, error } = await supabase
      .from('tournament_games')
      .insert(rows)
      .select(GAME_SELECT);

    if (error) throw new Error(error.message || 'Failed to create tournament games');
    return (data || []).map(normalizeGame);
  },

  async deleteGame(gameId) {
    const { error } = await supabase.from('tournament_games').delete().eq('id', gameId);
    if (error) throw new Error(error.message || 'Failed to delete tournament game');
  },

  async updateGame(gameId, { homeScore, awayScore, gameStatus, scheduledDate, venue } = {}) {
    const payload = {
      updated_at: new Date().toISOString(),
    };

    if (homeScore !== undefined) payload.home_score = parseInt(homeScore, 10) || 0;
    if (awayScore !== undefined) payload.away_score = parseInt(awayScore, 10) || 0;
    if (gameStatus !== undefined) payload.game_status = gameStatus;
    if (scheduledDate !== undefined) payload.scheduled_date = scheduledDate;
    if (venue !== undefined) payload.venue = venue;

    const { data, error } = await supabase
      .from('tournament_games')
      .update(payload)
      .eq('id', gameId)
      .select(GAME_SELECT)
      .single();

    if (error) throw new Error(error.message || 'Failed to update tournament game');
    return normalizeGame(data);
  },

  // ---------------------------------------------------------------------
  // Tournament container CRUD
  // ---------------------------------------------------------------------

  async getTournaments() {
    const { data, error } = await supabase
      .from('tournaments')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message || 'Failed to load tournaments');
    return (data || []).map(normalizeTournament);
  },

  async getTournamentById(tournamentId) {
    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        tournament_teams(
          id,
          tournament_id,
          regional_team_id,
          group_id,
          is_active,
          created_at,
          regional_team:regional_teams(id, team_name, logo_url, logo_path)
        ),
        tournament_groups(id, tournament_id, name, sort_order, created_at)
      `)
      .eq('id', tournamentId)
      .single();

    if (error) throw new Error(error.message || 'Failed to load tournament');
    return normalizeTournamentDetail(data);
  },

  async createTournament(tournamentData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    if (!tournamentData?.name?.trim()) {
      throw new Error('Tournament name is required');
    }

    const { data, error } = await supabase
      .from('tournaments')
      .insert({
        name: tournamentData.name.trim(),
        season: tournamentData.season?.trim() || null,
        description: tournamentData.description?.trim() || null,
        tournament_status: tournamentData.tournamentStatus || 'upcoming',
        start_date: tournamentData.startDate || null,
        end_date: tournamentData.endDate || null,
        max_teams: tournamentData.maxTeams ? parseInt(tournamentData.maxTeams, 10) : 16,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to create tournament');
    return normalizeTournament(data);
  },

  async updateTournament(tournamentId, tournamentData) {
    const payload = { updated_at: new Date().toISOString() };

    if (tournamentData.name !== undefined) payload.name = tournamentData.name?.trim();
    if (tournamentData.season !== undefined) payload.season = tournamentData.season?.trim() || null;
    if (tournamentData.description !== undefined) payload.description = tournamentData.description?.trim() || null;
    if (tournamentData.tournamentStatus !== undefined) payload.tournament_status = tournamentData.tournamentStatus;
    if (tournamentData.startDate !== undefined) payload.start_date = tournamentData.startDate || null;
    if (tournamentData.endDate !== undefined) payload.end_date = tournamentData.endDate || null;
    if (tournamentData.maxTeams !== undefined) {
      payload.max_teams = tournamentData.maxTeams ? parseInt(tournamentData.maxTeams, 10) : null;
    }

    const { data, error } = await supabase
      .from('tournaments')
      .update(payload)
      .eq('id', tournamentId)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to update tournament');
    return normalizeTournament(data);
  },

  async deleteTournament(tournamentId) {
    const { error } = await supabase.from('tournaments').delete().eq('id', tournamentId);
    if (error) throw new Error(error.message || 'Failed to delete tournament');
  },

  // ---------------------------------------------------------------------
  // Tournament teams
  // ---------------------------------------------------------------------

  async addTeam(tournamentId, regionalTeamId) {
    const { data, error } = await supabase
      .from('tournament_teams')
      .insert({
        tournament_id: tournamentId,
        regional_team_id: regionalTeamId,
      })
      .select(`
        *,
        regional_team:regional_teams(id, team_name, logo_url, logo_path)
      `)
      .single();

    if (error) throw new Error(error.message || 'Failed to add team to tournament');
    return normalizeTournamentTeam(data);
  },

  async removeTeam(tournamentId, regionalTeamId) {
    const { error } = await supabase
      .from('tournament_teams')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('regional_team_id', regionalTeamId);

    if (error) throw new Error(error.message || 'Failed to remove team from tournament');
  },

  async assignTeamToGroup(tournamentTeamId, groupId) {
    const { data, error } = await supabase
      .from('tournament_teams')
      .update({ group_id: groupId || null })
      .eq('id', tournamentTeamId)
      .select(`
        *,
        regional_team:regional_teams(id, team_name, logo_url, logo_path)
      `)
      .single();

    if (error) throw new Error(error.message || 'Failed to assign team to group');
    return normalizeTournamentTeam(data);
  },

  // ---------------------------------------------------------------------
  // Tournament groups
  // ---------------------------------------------------------------------

  async getGroups(tournamentId) {
    const { data, error } = await supabase
      .from('tournament_groups')
      .select('*')
      .eq('tournament_id', tournamentId)
      .order('sort_order', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load tournament groups');
    return (data || []).map(normalizeGroup);
  },

  async createGroup(tournamentId, name, sortOrder = 0) {
    if (!name?.trim()) throw new Error('Group name is required');

    const { data, error } = await supabase
      .from('tournament_groups')
      .insert({
        tournament_id: tournamentId,
        name: name.trim(),
        sort_order: sortOrder,
      })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to create group');
    return normalizeGroup(data);
  },

  async deleteGroup(groupId) {
    const { error } = await supabase.from('tournament_groups').delete().eq('id', groupId);
    if (error) throw new Error(error.message || 'Failed to delete group');
  },

  /**
   * Generate tournament groups and optionally distribute active teams.
   * @param {string} tournamentId
   * @param {Object} options
   * @param {number} options.groupCount - number of groups to create (2–16)
   * @param {'letters'|'numbers'} [options.naming='letters'] - Group A/B/C or Group 1/2/3
   * @param {boolean} [options.assignTeams=true] - distribute active teams into groups
   * @param {'balanced'|'random'} [options.distribution='balanced'] - snake draft vs shuffle
   * @param {boolean} [options.replaceExisting=false] - delete existing groups first
   */
  async generateGroups(tournamentId, options = {}) {
    const {
      groupCount,
      naming = 'letters',
      assignTeams = true,
      distribution = 'balanced',
      replaceExisting = false,
    } = options;

    const count = Number(groupCount);
    if (!tournamentId) throw new Error('Tournament ID is required');
    if (!Number.isInteger(count) || count < 2 || count > 16) {
      throw new Error('Number of groups must be between 2 and 16');
    }

    if (replaceExisting) {
      const existing = await this.getGroups(tournamentId);
      for (const group of existing) {
        await this.deleteGroup(group.id);
      }
    } else {
      const existing = await this.getGroups(tournamentId);
      if (existing.length > 0) {
        throw new Error(
          'Groups already exist. Enable "Replace existing groups" to regenerate, or delete them first.'
        );
      }
    }

    const groupNames = Array.from({ length: count }, (_, index) => {
      if (naming === 'numbers') return `Group ${index + 1}`;
      return `Group ${String.fromCharCode(65 + index)}`;
    });

    const { data: createdRows, error: createError } = await supabase
      .from('tournament_groups')
      .insert(
        groupNames.map((name, index) => ({
          tournament_id: tournamentId,
          name,
          sort_order: index,
        }))
      )
      .select();

    if (createError) throw new Error(createError.message || 'Failed to generate groups');

    const groups = (createdRows || [])
      .map(normalizeGroup)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    let assignedCount = 0;
    if (assignTeams && groups.length > 0) {
      const { data: teamRows, error: teamsError } = await supabase
        .from('tournament_teams')
        .select('id, is_active')
        .eq('tournament_id', tournamentId)
        .eq('is_active', true);

      if (teamsError) throw new Error(teamsError.message || 'Failed to load tournament teams');

      let teamIds = (teamRows || []).map((row) => row.id);
      if (distribution === 'random') {
        for (let i = teamIds.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [teamIds[i], teamIds[j]] = [teamIds[j], teamIds[i]];
        }
      }

      // Snake draft for balanced group sizes: A,B,C,C,B,A,...
      for (let i = 0; i < teamIds.length; i += 1) {
        const round = Math.floor(i / groups.length);
        const posInRound = i % groups.length;
        const groupIndex = round % 2 === 0 ? posInRound : groups.length - 1 - posInRound;
        const groupId = groups[groupIndex].id;

        const { error: assignError } = await supabase
          .from('tournament_teams')
          .update({ group_id: groupId })
          .eq('id', teamIds[i]);

        if (assignError) {
          throw new Error(assignError.message || 'Failed to assign teams to groups');
        }
        assignedCount += 1;
      }
    }

    return {
      groups,
      assignedCount,
    };
  },

  async getSelectedOfficials() {
    const { data, error } = await supabase
      .from('tournament_selected_officials')
      .select(`
        *,
        official:officials(
          id,
          specialization,
          license_number,
          user_profile:user_profiles!officials_user_profile_id_fkey(
            full_name,
            email,
            phone,
            avatar_url
          )
        )
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load tournament officials');
    return (data || []).map(normalizeSelectedOfficial);
  },

  async selectOfficial(officialId, assignedRole = null) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('tournament_selected_officials')
      .upsert(
        {
          official_id: officialId,
          assigned_role: assignedRole,
          is_active: true,
          selected_by: user.id,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'official_id' }
      )
      .select(`
        *,
        official:officials(
          id,
          specialization,
          license_number,
          user_profile:user_profiles!officials_user_profile_id_fkey(
            full_name,
            email,
            phone,
            avatar_url
          )
        )
      `)
      .single();

    if (error) throw new Error(error.message || 'Failed to select official');
    return normalizeSelectedOfficial(data);
  },

  async removeSelectedOfficial(selectionId) {
    const { error } = await supabase
      .from('tournament_selected_officials')
      .delete()
      .eq('id', selectionId);

    if (error) throw new Error(error.message || 'Failed to remove tournament official');
  },

  async getRegionalPlayerStatistics() {
    const { data: players, error: playersError } = await supabase
      .from('regional_team_players')
      .select(`
        id,
        full_name,
        position,
        age,
        jersey_number,
        photo_url,
        photo_path,
        regional_team_id,
        regional_teams(id, team_name, logo_url, logo_path, region)
      `)
      .order('full_name', { ascending: true });

    if (playersError) {
      throw new Error(playersError.message || 'Failed to load regional players');
    }

    const { data: stats, error: statsError } = await supabase
      .from('regional_player_statistics')
      .select('*');

    if (statsError) {
      throw new Error(statsError.message || 'Failed to load regional player statistics');
    }

    const statsByPlayer = (stats || []).reduce((acc, row) => {
      acc[row.regional_team_player_id] = row;
      return acc;
    }, {});

    return (players || []).map((player) => {
      const row = statsByPlayer[player.id];
      const team = player.regional_teams;
      return {
        playerId: player.id,
        fullName: player.full_name,
        position: player.position,
        age: player.age,
        jerseyNumber: player.jersey_number,
        photoUrl: resolveRegionalTeamLogo(player.photo_url, player.photo_path),
        teamId: player.regional_team_id,
        teamName: team?.team_name || '—',
        teamRegion: team?.region || null,
        teamLogoUrl: resolveRegionalTeamLogo(team?.logo_url, team?.logo_path),
        gamesPlayed: row?.games_played || 0,
        points: row?.points || 0,
        rebounds: row?.rebounds || 0,
        assists: row?.assists || 0,
        steals: row?.steals || 0,
        blocks: row?.blocks || 0,
        statsId: row?.id || null,
      };
    });
  },

  async upsertPlayerStatistics(playerId, stats) {
    const payload = {
      regional_team_player_id: playerId,
      games_played: parseInt(stats.gamesPlayed, 10) || 0,
      points: parseInt(stats.points, 10) || 0,
      rebounds: parseInt(stats.rebounds, 10) || 0,
      assists: parseInt(stats.assists, 10) || 0,
      steals: parseInt(stats.steals, 10) || 0,
      blocks: parseInt(stats.blocks, 10) || 0,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('regional_player_statistics')
      .upsert(payload, { onConflict: 'regional_team_player_id' })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to save player statistics');
    return data;
  },
};
